import { useEffect, useRef, useState } from 'react'
import { normaliser } from './Filtre.jsx'

// Un seul champ pour trouver quelqu'un ou l'ajouter.
//
// Avant, il fallait savoir d'avance si la personne avait déjà un compte : la
// chercher dans une liste, ou cliquer « ajouter quelqu'un qui n'a pas encore de
// compte ». Personne ne sait ça de tête dans une intercommunalité. On tape un
// nom, et la réponse vient : soit elle apparaît, soit on propose de la créer.
//
// La recherche se fait sur la liste déjà chargée, sans aller-retour réseau :
// les propositions suivent la frappe sans latence.
export default function ChercherPersonne({ gens, onChoisir, onCreer, placeholder }) {
  const [q, setQ] = useState('')
  const [ouvert, setOuvert] = useState(false)
  const [surligne, setSurligne] = useState(0)
  const champ = useRef(null)

  const requete = normaliser(q).trim()
  const mots = requete.split(/\s+/).filter(Boolean)
  const trouves = mots.length
    ? gens.filter((p) => {
        const foin = normaliser(`${p.name} ${p.email || ''}`)
        return mots.every((m) => foin.includes(m))
      }).slice(0, 8)
    : []

  // Proposer la création dès qu'on a tapé quelque chose qui ne correspond à
  // personne exactement : c'est le cas le plus fréquent d'un nouvel arrivant.
  const exact = trouves.some((p) => normaliser(p.name) === requete)
  const creable = onCreer && requete.length >= 2 && !exact
  const options = [...trouves, ...(creable ? [{ creer: true }] : [])]
  const montrer = ouvert && options.length > 0

  useEffect(() => { setSurligne(0) }, [q])

  function valider(option) {
    if (!option) return
    if (option.creer) onCreer(devine(q))
    else onChoisir(option)
    setQ(''); setOuvert(false)
  }

  function clavier(e) {
    if (!montrer) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setSurligne((i) => (i + 1) % options.length) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSurligne((i) => (i - 1 + options.length) % options.length) }
    if (e.key === 'Enter') { e.preventDefault(); valider(options[surligne]) }
    if (e.key === 'Escape') { setOuvert(false) }
  }

  return (
    <div className="chercher-personne">
      <label className="sr" htmlFor="cp">Chercher ou ajouter une personne</label>
      <input id="cp" ref={champ} type="search" value={q} autoComplete="off"
             role="combobox" aria-expanded={montrer} aria-controls="cp-liste"
             aria-activedescendant={montrer ? `cp-${surligne}` : undefined}
             placeholder={placeholder || 'Chercher une personne, ou taper un nouveau nom…'}
             onChange={(e) => { setQ(e.target.value); setOuvert(true) }}
             onFocus={() => setOuvert(true)}
             onKeyDown={clavier}
             onBlur={() => setTimeout(() => setOuvert(false), 150)} />

      {montrer && (
        <ul id="cp-liste" role="listbox">
          {trouves.map((p, i) => (
            <li key={p.id} id={`cp-${i}`} role="option" aria-selected={i === surligne}
                className={i === surligne ? 'on' : ''}>
              <button onMouseDown={() => valider(p)} onMouseEnter={() => setSurligne(i)}>
                <span className="qui">
                  <strong>{p.name}</strong>
                  {p.email && <span className="mono">{p.email}</span>}
                </span>
                <span className={`etat ${p.dedans || p.acces ? 'oui' : ''}`}>
                  {p.dedans || p.acces ? 'a déjà accès' : 'donner accès'}
                </span>
              </button>
            </li>
          ))}
          {creable && (
            <li id={`cp-${trouves.length}`} role="option" className={`creer ${surligne === trouves.length ? 'on' : ''}`}
                aria-selected={surligne === trouves.length}>
              <button onMouseDown={() => valider({ creer: true })}
                      onMouseEnter={() => setSurligne(trouves.length)}>
                <span className="qui">
                  <strong>Ajouter « {q.trim()} »</strong>
                  <span>Cette personne n'a pas encore de compte</span>
                </span>
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

// Ce qui a été tapé est-il un nom ou une adresse ? Autant préremplir le bon
// champ plutôt que de faire retaper.
function devine(saisie) {
  const texte = (saisie || '').trim()
  return texte.includes('@') ? { name: '', email: texte } : { name: texte, email: '' }
}
